import { LogLevel } from "./Enum";
import { getTimestamp } from "./DateTime";

/**
 * Minimal logger configuration
 */
interface LoggerConfig {
  level: LogLevel;
  enableConsole: boolean;
  prefix?: string;
}

/**
 * Logger utility
 */
class LogManager {
  private config: LoggerConfig = {
    level: LogLevel.DEBUG,
    enableConsole: true,
    prefix: "",
  };

  /**
   * Configure logger
   */
  configure(config: Partial<LoggerConfig>): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Set minimum log level
   */
  setLevel(level: LogLevel): void {
    this.config.level = level;
  }

  /**
   * Get current log level
   */
  getLevel(): LogLevel {
    return this.config.level;
  }

  /**
   * Check if message should be logged based on level
   */
  private shouldLog(level: LogLevel): boolean {
    const levelValues = {
      [LogLevel.ERROR]: 40,
      [LogLevel.WARNING]: 30,
      [LogLevel.INFO]: 20,
      [LogLevel.FINE]: 10,
      [LogLevel.DEBUG]: 0,
    };

    return levelValues[level] >= levelValues[this.config.level];
  }

  /**
   * Format message for console output
   */
  private formatMessage(message: string, tags?: string[]): string {
    const timestamp = getTimestamp();
    const tagString = tags?.length ? `[${tags.join(",")}] ` : "";
    const prefixString = this.config.prefix ? `${this.config.prefix} ` : "";

    return `${timestamp} ${prefixString}${tagString}${message}`;
  }

  /**
   * Core logging method
   */
  private log(
    message: string,
    level: LogLevel,
    error?: unknown,
    tags?: string[],
  ): void {
    if (!this.shouldLog(level) || !this.config.enableConsole) return;

    const formatted = this.formatMessage(message, tags);

    switch (level) {
      case LogLevel.ERROR:
        if (error) {
          console.error(formatted, error);
        } else {
          console.error(formatted);
        }
        break;
      case LogLevel.WARNING:
        console.warn(formatted);
        break;
      case LogLevel.INFO:
        console.info(formatted);
        break;
      case LogLevel.FINE:
      case LogLevel.DEBUG:
      default:
        console.debug(formatted);
        break;
    }
  }

  /**
   * Log debug message
   */
  debug(message: string, tags?: string[]): void {
    this.log(message, LogLevel.DEBUG, undefined, tags);
  }

  /**
   * Log info message
   */
  info(message: string, tags?: string[]): void {
    this.log(message, LogLevel.INFO, undefined, tags);
  }

  /**
   * Log warning message
   */
  warn(message: string, tags?: string[]): void {
    this.log(message, LogLevel.WARNING, undefined, tags);
  }

  /**
   * Log error message
   */
  error(message: string, error?: unknown, tags?: string[]): void {
    this.log(message, LogLevel.ERROR, error, tags);
  }
}

// Create singleton instance
const logger = new LogManager();

export const Logger = {
  debug: (message: string, tags?: string[]): void =>
    logger.debug(message, tags),
  info: (
    message: string,
    tags?: string[],
    p0?: Record<string, unknown>,
  ): void => {
    logger.info(
      p0 && Object.keys(p0).length > 0 
        ? `${message} (Context: ${JSON.stringify(p0)})` 
        : message,
      tags
    );
  },
  warn: (message: string, tags?: string[]): void => logger.warn(message, tags),
  error: (message: string, err?: unknown, tags?: string[]): void =>
    logger.error(message, err, tags),
  configure: (config: Partial<LoggerConfig>): void => logger.configure(config),
  setLevel: (level: LogLevel): void => logger.setLevel(level),
  getLevel: (): LogLevel => logger.getLevel(),
};

// Default export as Logger object
export default Logger;

// Export types
export type { LoggerConfig };
